import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useMutation, useQuery } from 'react-apollo';

import { ORIENTATION } from '../../../services/cards/constants';
import PrintProductConfigurator from '../../../services/prints/components/PrintProductConfigurator';
import getAccountCardQuery from '../../../services/cards/graphql/queries/getAccountCard';
import Spinner from '../../../components/global/Spinner';
import ImageUpload from '../../../components/global/ImageUpload';
import useAssetImageUpload from '../../../hooks/useAssetImageUpload';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import { PRINT_ASSET_TYPES } from '../../../constants/print-assets';
import { accountUpdate as accountUpdateMutation } from '../../../graphql/mutations/account';
import useAccount from '../../../components/global/hooks/useAccount';
import Label from '../../../components/global/Label';
import uuidv4 from '../../../util/uuid';

const propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      accountCardId: PropTypes.string,
    }),
  }),
};

const AccountCardPrintProductConfigurationView = ({ match }) => {
  const [uploadedBackdrop, setUploadedBackdrop] = useState(null);
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const { account } = useAccount();

  const { data: { accountCard } = {} } = useQuery(getAccountCardQuery, {
    variables: {
      id: match.params.accountCardId,
    },
    skip: !match.params.accountCardId,
    fetchPolicy: 'cache-and-network',
  });

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    printAssetType: PRINT_ASSET_TYPES.BACKGROUND,
    onImageUploaded: async assetImage => {
      const {
        data: {
          accountUpdate: { account: updatedAccount },
        },
      } = await accountUpdate({
        variables: {
          input: {
            id: account.id,
            logo_image: {
              asset_image_id: assetImage.id,
            },
          },
        },
      });
      setUploadedBackdrop({
        id: uuidv4(),
        asset_image: updatedAccount.logo_image,
      });
    },
  });

  if (!accountCard) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  const printBackgrounds = uploadedBackdrop ? [uploadedBackdrop] : [];

  return (
    <div>
      <div className="backdrop-upload-container">
        <Label id="backdrop-upload">Backdrop Image</Label>
        <ImageUpload
          id="backdrop-upload"
          buttonText="Upload a backdrop"
          loading={uploading}
          disabled={uploading}
          image={
            uploadedBackdrop?.asset_image
              ? {
                  id: uploadedBackdrop.asset_image.id,
                  thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(size => ({
                    size_code: size,
                    url: uploadedBackdrop.asset_image.url,
                    width: 0,
                    height: 0,
                  })),
                }
              : null
          }
          onInputChange={event => {
            const [file] = event.target.files;
            onUpload(file);
          }}
          onImageDismiss={() => {
            onUploadReset();
            setUploadedBackdrop(null);
          }}
        />
      </div>

      <PrintProductConfigurator
        forceOpen
        storeFetchedAccountCard
        printProductId={
          accountCard.print_product_configuration.print_product.id
        }
        printProductSizeId={
          accountCard.print_product_configuration.print_product_size.id
        }
        printProductSizeServiceIds={(
          accountCard.print_product_configuration.print_product_size_services
            .records ?? []
        ).map(service => service.id)}
        availablePrintProductSizeIds={[
          accountCard.print_product_configuration.print_product_size.id,
        ]}
        orientation={
          accountCard.account_card_sides?.[0].card_side?.card_template
            ?.orientation ?? ORIENTATION.LANDSCAPE
        }
        onClose={() => {}}
        onComplete={() => {}}
        buttonText="Done"
        defaultAccountCard={accountCard}
        disablePhotoWatermarkOverlay
        allowPhotoChange
        printBackgrounds={printBackgrounds}
      />
    </div>
  );
};

AccountCardPrintProductConfigurationView.propTypes = propTypes;

export default withRouter(AccountCardPrintProductConfigurationView);
